import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Accessibility,
  Users,
  GraduationCap,
  Building2,
  TrendingUp,
  Award,
  Heart,
  Layers,
  DoorOpen,
} from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';
import {
  getCwsnStudentSummary,
  getCwsnTeacherSummary,
  getSchoolRampSummary,
  CwsnStudentResponse,
  CwsnTeacherResponse,
  RampResponse,
  CwsnStudentSchool,
  CwsnTeacherSchool
} from '../../services/api';

/**
 * Unwrap one settled request. A single failing endpoint must not discard the
 * results that did arrive, so each is read independently and a rejection just
 * leaves that one dataset null.
 */
const settledValue = <T,>(result: PromiseSettledResult<T>, label: string): T | null => {
  if (result.status === 'fulfilled') return result.value;
  console.error(`Error fetching ${label}:`, result.reason);
  return null;
};

export default function CWSNProfileAnalysis() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'students' | 'educators' | 'infrastructure'>('students');
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
    infraData?: any;
    tabLabel?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const [studentData, setStudentData] = useState<CwsnStudentResponse | null>(null);
  const [teacherData, setTeacherData] = useState<CwsnTeacherResponse | null>(null);
  const [rampData, setRampData] = useState<RampResponse | null>(null);
  
  // Dummy data for infrastructure - will be replaced with API data
  const [infraData] = useState({
    floorsInBuilding: {
      PMShree: { singleFloor: 45, twoFloor: 78, multiFloor: 32 },
      NonPMShree: { singleFloor: 312, twoFloor: 445, multiFloor: 156 },
      Total: { singleFloor: 357, twoFloor: 523, multiFloor: 188 }
    },
    toiletForBoys: {
      PMShree: { groundFloor: 142, allFloors: 98 },
      NonPMShree: { groundFloor: 756, allFloors: 542 },
      Total: { groundFloor: 898, allFloors: 640 }
    },
    toiletForGirls: {
      PMShree: { groundFloor: 138, allFloors: 95 },
      NonPMShree: { groundFloor: 748, allFloors: 535 },
      Total: { groundFloor: 886, allFloors: 630 }
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [students, teachers, ramps] = await Promise.allSettled([
          getCwsnStudentSummary(),
          getCwsnTeacherSummary(),
          getSchoolRampSummary()
        ]);

        setStudentData(settledValue(students, 'CWSN student summary'));
        setTeacherData(settledValue(teachers, 'CWSN teacher summary'));
        setRampData(settledValue(ramps, 'school ramp summary'));
      } catch (error) {
        console.error('Error fetching CWSN data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const tabs = [
    { id: 'students', label: 'CWSN Students', icon: Users },
    { id: 'educators', label: 'Special Educators', icon: GraduationCap },
    { id: 'infrastructure', label: 'Infrastructure', icon: Building2 },
  ];

  const addPercentage = (items: { category: string; pmshriCount: number; nonPmshriCount: number; total: number }[]) =>
    items.map(item => ({
      ...item,
      pmshriPercentage: item.total > 0 ? (item.pmshriCount / item.total) * 100 : 0
    }));

  const getComparisonData = () => {
    if (!studentData || !teacherData || !rampData) return [];

    switch (activeTab) {
      case 'students':
        return addPercentage([
          {
            category: 'Primary',
            pmshriCount: studentData.summary.PMShree.primary,
            nonPmshriCount: studentData.summary.NonPMShree.primary,
            total: studentData.summary.Total.primary
          },
          {
            category: 'Secondary',
            pmshriCount: studentData.summary.PMShree.secondary,
            nonPmshriCount: studentData.summary.NonPMShree.secondary,
            total: studentData.summary.Total.secondary
          },
        ]);
      case 'educators':
        return addPercentage([
          {
            category: 'Primary Teachers',
            pmshriCount: teacherData.summary.PMShree.primaryTeachers,
            nonPmshriCount: teacherData.summary.NonPMShree.primaryTeachers,
            total: teacherData.summary.Total.primaryTeachers
          },
          {
            category: 'Secondary Teachers',
            pmshriCount: teacherData.summary.PMShree.secondaryTeachers,
            nonPmshriCount: teacherData.summary.NonPMShree.secondaryTeachers,
            total: teacherData.summary.Total.secondaryTeachers
          },
        ]);
      case 'infrastructure':
        return addPercentage([
          {
            category: 'Ramp (Ground Floor)',
            pmshriCount: rampData.summary.PMShree.rampInGroundFloor,
            nonPmshriCount: rampData.summary.NonPMShree.rampInGroundFloor,
            total: rampData.summary.Total.rampInGroundFloor
          },
          {
            category: 'Ramp (All Floors)',
            pmshriCount: rampData.summary.PMShree.rampInAllFloor,
            nonPmshriCount: rampData.summary.NonPMShree.rampInAllFloor,
            total: rampData.summary.Total.rampInAllFloor
          },
          {
            category: 'Multi-Floor Buildings',
            pmshriCount: infraData.floorsInBuilding.PMShree.multiFloor,
            nonPmshriCount: infraData.floorsInBuilding.NonPMShree.multiFloor,
            total: infraData.floorsInBuilding.Total.multiFloor
          },
          {
            category: 'Boys Toilet (Ground)',
            pmshriCount: infraData.toiletForBoys.PMShree.groundFloor,
            nonPmshriCount: infraData.toiletForBoys.NonPMShree.groundFloor,
            total: infraData.toiletForBoys.Total.groundFloor
          },
          {
            category: 'Girls Toilet (Ground)',
            pmshriCount: infraData.toiletForGirls.PMShree.groundFloor,
            nonPmshriCount: infraData.toiletForGirls.NonPMShree.groundFloor,
            total: infraData.toiletForGirls.Total.groundFloor
          },
        ]);
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7]">
        <div className="text-xl text-purple-700 font-semibold">Loading CWSN Analytics...</div>
      </div>
    );
  }

  // A dataset the page cannot render without is missing. Say so — this used to
  // fall back into the loader above, which read as a hung page rather than a
  // failed request.
  if (!studentData || !teacherData || !rampData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f5f5f7] px-4 text-center">
        <div className="text-xl text-red-600 font-semibold">CWSN analytics could not be loaded.</div>
        <div className="text-sm text-gray-500 mt-2">Some data is unavailable right now. Please try again later.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Accessibility className="w-7 h-7" />
              CWSN Profile Analysis
            </h1>
            <p className="text-gray-600">
              Children with Special Needs - Students, educators, infrastructure, and support equipment
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Analytics
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard
            title="CWSN Students"
            value={studentData.summary.Total.totalCwsn.toLocaleString()}
            icon={<Heart className="w-6 h-6" />}
            color="pink"
            subtitle="Across all KVs"
          />
          <StatCard
            title="Special Educators"
            value={teacherData.summary.Total.totalTrainedTeachers.toLocaleString()}
            icon={<GraduationCap className="w-6 h-6" />}
            color="purple"
            subtitle="Trained teachers"
          />
          <StatCard
            title="Schools with Ramps"
            value={rampData.summary.Total.schoolsWithRamps.toLocaleString()}
            icon={<Building2 className="w-6 h-6" />}
            color="blue"
            subtitle={`${((rampData.summary.Total.schoolsWithRamps / rampData.summary.Total.schoolCount) * 100).toFixed(1)}% of schools`}
          />
          <StatCard
            title="Multi-Floor Buildings"
            value={infraData.floorsInBuilding.Total.multiFloor.toLocaleString()}
            icon={<Layers className="w-6 h-6" />}
            color="purple"
            subtitle="3+ floors"
          />
          <StatCard
            title="Accessible Toilets"
            value={(infraData.toiletForBoys.Total.groundFloor + infraData.toiletForGirls.Total.groundFloor).toLocaleString()}
            icon={<DoorOpen className="w-6 h-6" />}
            color="teal"
            subtitle="Ground floor"
          />
        </div>

        {/* Overview Cards Based on Active Tab */}
        {activeTab === 'students' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              CWSN Student Distribution
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 text-center">
                <p className="text-sm text-gray-500 mb-1">Primary Level</p>
                <p className="text-2xl font-bold text-gray-800">{studentData.summary.Total.primary.toLocaleString()}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200 text-center">
                <p className="text-sm text-gray-500 mb-1">Secondary Level</p>
                <p className="text-2xl font-bold text-gray-800">{studentData.summary.Total.secondary.toLocaleString()}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 text-center">
                <p className="text-sm text-gray-500 mb-1">Total Students</p>
                <p className="text-2xl font-bold text-gray-800">{studentData.summary.Total.totalCwsn.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'infrastructure' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Infrastructure Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-sm text-gray-500 mb-1">Single Floor</p>
                <p className="text-2xl font-bold text-gray-800">{infraData.floorsInBuilding.Total.singleFloor}</p>
                <p className="text-xs text-gray-600 mt-1">Buildings</p>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                <p className="text-sm text-gray-500 mb-1">Two Floors</p>
                <p className="text-2xl font-bold text-gray-800">{infraData.floorsInBuilding.Total.twoFloor}</p>
                <p className="text-xs text-gray-600 mt-1">Buildings</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <p className="text-sm text-gray-500 mb-1">Multi-Floor (3+)</p>
                <p className="text-2xl font-bold text-gray-800">{infraData.floorsInBuilding.Total.multiFloor}</p>
                <p className="text-xs text-gray-600 mt-1">Buildings</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-sm text-gray-500 mb-1">Total Buildings</p>
                <p className="text-2xl font-bold text-gray-800">
                  {infraData.floorsInBuilding.Total.singleFloor + 
                   infraData.floorsInBuilding.Total.twoFloor + 
                   infraData.floorsInBuilding.Total.multiFloor}
                </p>
                <p className="text-xs text-gray-600 mt-1">Across all KVs</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                <p className="text-sm text-gray-500 mb-2 font-medium">Boys Toilets</p>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-600">Ground Floor</p>
                    <p className="text-xl font-bold text-gray-800">{infraData.toiletForBoys.Total.groundFloor}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">All Floors</p>
                    <p className="text-xl font-bold text-gray-800">{infraData.toiletForBoys.Total.allFloors}</p>
                  </div>
                </div>
              </div>
              <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                <p className="text-sm text-gray-500 mb-2 font-medium">Girls Toilets</p>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-gray-600">Ground Floor</p>
                    <p className="text-xl font-bold text-gray-800">{infraData.toiletForGirls.Total.groundFloor}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">All Floors</p>
                    <p className="text-xl font-bold text-gray-800">{infraData.toiletForGirls.Total.allFloors}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                      ? 'text-purple-700 border-purple-600 bg-purple-50'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ComparisonBarChart
            data={getComparisonData()}
            title={`${tabs.find(t => t.id === activeTab)?.label} - PM-SHRI vs Non PM-SHRI`}
            xAxisLabel="Category"
          />
          <DistributionPieChart
            data={getComparisonData().map(d => ({ name: d.category, value: d.total }))}
            title={`${tabs.find(t => t.id === activeTab)?.label} Distribution`}
          />
        </div>

        {/* Detailed Table */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {tabs.find(t => t.id === activeTab)?.label} Details
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Category</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-green-600">PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-blue-600">Non PM-SHRI</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-purple-600">Total</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold text-gray-600">PM-SHRI %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {getComparisonData().map((row) => (
                  <tr key={row.category} className="hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.category}</td>
                    <td className="py-3 px-4 text-sm text-center text-green-600 font-medium">{row.pmshriCount.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-blue-600 font-medium">{row.nonPmshriCount.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center text-purple-600 font-bold">{row.total.toLocaleString()}</td>
                    <td className="py-3 px-4 text-sm text-center">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {((row.pmshriCount / row.total) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Schools Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Award className="w-5 h-5 text-blue-600" />
                Top Schools by CWSN Students
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {studentData.schools.slice(0, 10).map((school, index) => (
                <div key={school.schoolID} className="px-6 py-4 hover:bg-gray-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: school, tabLabel: 'CWSN Students' })}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-50 text-gray-500'
                        }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-800">{school.schoolName}</h4>
                          {school.isPMShree && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">PM-SHRI</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{school.stateName} • {school.roName}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{school.totalCwsnStudents} Students</span>
                          <span className="text-xs bg-purple-50 text-purple-600 px-2 py-0.5 rounded">Primary: {school.studentInPrimary}</span>
                          <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded">Secondary: {school.studentInSecondary}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-teal-50 border-b border-green-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Schools with Special Educators
              </h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {teacherData.schools.slice(0, 10).map((school, index) => (
                <div key={school.schoolID} className="px-6 py-4 hover:bg-green-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: school, tabLabel: 'Special Educators' })}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${index === 0 ? 'bg-yellow-100 text-yellow-700' :
                          index === 1 ? 'bg-gray-100 text-gray-600' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-50 text-gray-500'
                        }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-800">{school.schoolName}</h4>
                          {school.isPMShree && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">PM-SHRI</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{school.stateName} • {school.roName}</p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{school.totalTrainedTeachers} Educators</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detailed Analysis Reports Section */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure', path: '/kvs-samagam/analytics/infrastructure', icon: '🏫', color: 'blue' },
              { name: 'Enrollment', path: '/kvs-samagam/analytics/enrollment', icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile', path: '/kvs-samagam/analytics/ict-profile', icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile', icon: '📚', color: 'orange' },
              { name: 'CWSN Profile', path: '/kvs-samagam/analytics/cwsn-profile', icon: '♿', color: 'pink' },
              { name: 'NCC Profile', path: '/kvs-samagam/analytics/ncc-profile', icon: '🎖️', color: 'cyan' },
              { name: 'Sports', path: '/kvs-samagam/analytics/sports', icon: '🏆', color: 'yellow' },
              { name: 'Green Building', path: '/kvs-samagam/analytics/green-building', icon: '🌿', color: 'teal' },
              { name: 'Safety', path: '/kvs-samagam/analytics/safety', icon: '🛡️', color: 'red' },
              { name: 'Special Labs', path: '/kvs-samagam/analytics/special-labs', icon: '🔬', color: 'indigo' },
              { name: 'Water Profile', path: '/kvs-samagam/analytics/miscellaneous', icon: '💧', color: 'teal' },
            ].map((item) => {
              const isCurrentPage = item.name === 'CWSN Profile';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
